/**
 * Cross-buyer projects table — operational view of every manual project
 * regardless of buyer. The /admin/buyers page handles buyer config; this
 * page is the daily-driver for monitoring projects in flight.
 *
 * Columns include the financial rollup the API returns
 * (revenue / incentive / margin / completes / remaining) plus a status
 * pill that doubles as an inline transition control.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronDown } from 'lucide-react';
import {
  fetchProjects,
  updateManualProject,
  type ProjectSummary,
} from '../../lib/adminApi';

const PROJECTS_QUERY_KEY = ['admin', 'projects'] as const;

export default function AdminProjects() {
  const [searchParams] = useSearchParams();
  const buyerIdFilter = searchParams.get('buyer_id') ?? undefined;

  const query = useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, { buyerId: buyerIdFilter ?? null }],
    queryFn: () => fetchProjects(buyerIdFilter),
    // Auto-refresh while any project is LIVE so revenue/margin numbers
    // stay current without admins manually reloading. When everything
    // is paused/closed the polling is wasted work — turn it off.
    refetchInterval: (q) => {
      const data = q.state.data as { projects: ProjectSummary[] } | undefined;
      const anyLive = data?.projects?.some((p) => p.status === 'LIVE');
      return anyLive ? 60_000 : false;
    },
  });

  const projects = query.data?.projects ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-white">Projects</h1>
          {buyerIdFilter && (
            <p className="text-xs text-slate-500 mt-1">
              Filtered to one buyer.{' '}
              <Link to="/admin/projects" className="text-primary hover:underline">
                Show all
              </Link>
            </p>
          )}
        </div>
        <Link
          to={
            buyerIdFilter
              ? `/admin/manual-projects/new?buyer_id=${encodeURIComponent(buyerIdFilter)}`
              : '/admin/manual-projects/new'
          }
          className="inline-flex items-center gap-2 bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" /> New project
        </Link>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        {query.isLoading ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : query.isError ? (
          <div className="p-6 text-red-400 text-sm">
            {query.error instanceof Error ? query.error.message : 'Failed to load projects'}
          </div>
        ) : projects.length === 0 ? (
          <div className="p-6 text-slate-400 text-sm">
            No projects yet. Create the first one — buyer dropdown is on the new-project form.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Reference</th>
                <th className="text-left px-4 py-3">Buyer</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Target</th>
                <th className="text-right px-4 py-3">Completes</th>
                <th className="text-right px-4 py-3">Remaining</th>
                <th className="text-right px-4 py-3">CPI</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th
                  className="text-right px-4 py-3"
                  title="Member payouts: completions + consolation"
                >
                  Incentive
                </th>
                <th className="text-right px-4 py-3">Margin</th>
                <th className="text-left px-4 py-3">IR</th>
                <th className="text-left px-4 py-3">LOI</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <ProjectRow key={p.id} project={p} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ProjectRow({ project: p }: { project: ProjectSummary }) {
  // Target = total_completes + completes_remaining since the underlying
  // column is decremented per complete (Phase 5 fix). The original
  // requested target isn't stored separately — reconstructing it here
  // means a 100-complete project always shows "100" even after 30 fills.
  const target = p.total_completes + p.completes_remaining;

  // Observed IR = total_completes / total_starts. The API doesn't ship
  // this pre-computed because the math is trivial and admins like to
  // see the underlying counts in the same row when debugging.
  const observedIr = p.total_starts > 0
    ? Math.round((p.total_completes / p.total_starts) * 1000) / 10
    : null;

  // TODO: observed LOI lives in /stats (median_completion_minutes) which
  // is a per-project endpoint. Showing it in this list would mean N
  // round-trips on render. Project detail's StatsPanel already surfaces
  // it; revisit if/when we batch the median into the list endpoint.

  return (
    <tr className="border-t border-slate-700 hover:bg-slate-700/40">
      <td className="px-4 py-3">
        <Link
          to={`/admin/manual-projects/${p.id}`}
          className="text-white font-medium hover:underline"
        >
          {p.survey_name}
        </Link>
      </td>
      <td className="px-4 py-3 text-slate-300">
        {p.buyer ? (
          <Link
            to={`/admin/buyers/${p.buyer.id}`}
            className="hover:underline"
          >
            {p.buyer.name}
          </Link>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusPill projectId={p.id} status={p.status} />
      </td>
      <td className="px-4 py-3 text-slate-300 tabular-nums text-right">{target.toLocaleString()}</td>
      <td className="px-4 py-3 text-slate-300 tabular-nums text-right">
        {p.total_completes.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-slate-300 tabular-nums text-right">
        {p.completes_remaining.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-slate-300 tabular-nums text-right">${p.cpi.toFixed(2)}</td>
      <td className="px-4 py-3 text-slate-300 tabular-nums text-right">
        ${p.revenue_usd.toFixed(2)}
      </td>
      <td
        className="px-4 py-3 text-slate-300 tabular-nums text-right"
        title="Member payouts: completions + consolation"
      >
        ${p.incentive_usd.toFixed(2)}
      </td>
      <td
        className={`px-4 py-3 tabular-nums text-right font-medium ${
          p.margin_usd > 0
            ? 'text-green-400'
            : p.margin_usd < 0
            ? 'text-red-400'
            : 'text-slate-300'
        }`}
      >
        ${p.margin_usd.toFixed(2)}
      </td>
      <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
        {observedIr !== null ? `${observedIr}% obs` : '—'}{' '}
        <span className="text-slate-500">({p.ir}% sold)</span>
      </td>
      <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
        <span className="text-slate-500">{p.loi} min sold</span>
      </td>
      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
        {new Date(p.created_at).toLocaleDateString()}
      </td>
    </tr>
  );
}

const STATUS_TRANSITIONS: Record<NonNullable<ProjectSummary['status']>, Array<NonNullable<ProjectSummary['status']>>> = {
  LIVE: ['PAUSED', 'CLOSED'],
  PAUSED: ['LIVE', 'CLOSED'],
  CLOSED: [],
};

const STATUS_STYLES: Record<NonNullable<ProjectSummary['status']>, string> = {
  LIVE: 'bg-green-900/50 text-green-400 hover:bg-green-900/70',
  PAUSED: 'bg-amber-900/50 text-amber-400 hover:bg-amber-900/70',
  CLOSED: 'bg-slate-700 text-slate-400 cursor-default',
};

const STATUS_LABEL: Record<NonNullable<ProjectSummary['status']>, string> = {
  LIVE: 'Live',
  PAUSED: 'Paused',
  CLOSED: 'Closed',
};

const ACTION_LABEL: Record<NonNullable<ProjectSummary['status']>, string> = {
  LIVE: 'Resume',
  PAUSED: 'Pause',
  CLOSED: 'Close',
};

function StatusPill({
  projectId,
  status,
}: {
  projectId: string;
  status: ProjectSummary['status'];
}) {
  // Treat null status as PAUSED (legacy rows from before the enum was
  // wired — same fallback the PATCH handler uses on the API side).
  const current = (status ?? 'PAUSED') as NonNullable<ProjectSummary['status']>;

  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside dismiss. Don't bother with focus traps; this is admin-
  // only UI and the menu only has 0–2 items.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const mutation = useMutation({
    mutationFn: (next: NonNullable<ProjectSummary['status']>) =>
      updateManualProject(projectId, { status: next }),
    onSuccess: () => {
      // Invalidate the cross-buyer list AND any per-buyer scopes —
      // the projects query key carries the buyer filter, so the
      // root prefix invalidates them all in one shot.
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyer', undefined, 'projects'] });
      setOpen(false);
    },
  });

  const transitions = STATUS_TRANSITIONS[current];
  const interactive = transitions.length > 0 && !mutation.isPending;

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        onClick={() => interactive && setOpen((o) => !o)}
        disabled={!interactive}
        className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 transition-colors ${STATUS_STYLES[current]} ${
          !interactive ? 'cursor-default' : ''
        }`}
      >
        {STATUS_LABEL[current]}
        {interactive && <ChevronDown className="w-3 h-3" />}
      </button>
      {open && interactive && (
        <div className="absolute z-10 mt-1 left-0 bg-slate-900 border border-slate-700 rounded-lg shadow-lg overflow-hidden min-w-[140px]">
          {transitions.map((next) => (
            <Fragment key={next}>
              <button
                onClick={() => mutation.mutate(next)}
                disabled={mutation.isPending}
                className="block w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white disabled:opacity-50"
              >
                {ACTION_LABEL[next]}
              </button>
            </Fragment>
          ))}
        </div>
      )}
      {mutation.isError && (
        <p className="absolute top-full left-0 mt-1 text-[10px] text-red-400 whitespace-nowrap">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed'}
        </p>
      )}
    </div>
  );
}
